import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Download, ChevronLeft, TrendingUp, TrendingDown, 
  Target, FileText, CheckCircle, XCircle, AlertTriangle,
  Settings, Percent, DollarSign, BarChart3, Zap, RefreshCw, X, FileSpreadsheet, Infinity
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent, formatNumber, cn } from '../lib/utils';
import { exportScenariosToExcel } from './ExportExcelButton';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, ReferenceLine, Cell, ComposedChart, Line, Area, LabelList
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format capped percentages
const formatCappedPercent = (value, cap = 10000) => {
  if (value > cap) return `+${cap.toLocaleString()}%`;
  if (value < -cap) return `-${cap.toLocaleString()}%`;
  return `${value.toFixed(1)}%`;
};

export const ScenariosPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, getCalculation } = useProject();
  const { formatChartValue } = useChartPreferences();
  
  const [baseValuation, setBaseValuation] = useState(null);
  const [breakeven, setBreakeven] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scenarios');
  const [downloading, setDownloading] = useState(false);
  const [downloadingComplete, setDownloadingComplete] = useState(false);
  const [incomeYears, setIncomeYears] = useState([]);
  const [backendScenarios, setBackendScenarios] = useState(null);
  
  // User-defined ranges
  const [salesVariation, setSalesVariation] = useState(20);
  const [costVariation, setCostVariation] = useState(10);
  
  // Breakeven view filter
  const [breakevenView, setBreakevenView] = useState('anual');
  
  // Popup state
  const [selectedCell, setSelectedCell] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        
        const [valuationRes, breakevenRes, incomeRes, scenariosRes] = await Promise.all([
          getCalculation(projectId, 'valuation'),
          axios.get(`${API}/projects/${projectId}/calculations/breakeven`),
          axios.get(`${API}/projects/${projectId}/calculations/income-statement`),
          axios.get(`${API}/projects/${projectId}/calculations/scenarios?sales_variation=${salesVariation}&cost_variation=${costVariation}`)
        ]);
        
        setBaseValuation(valuationRes);
        setBreakeven(breakevenRes.data);
        setIncomeYears(Array.isArray(incomeRes.data) ? incomeRes.data : incomeRes.data?.years || []);
        setBackendScenarios(scenariosRes.data);
      } catch (err) {
        console.error('Error loading data:', err);
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  // Refetch backend scenarios when sliders change (debounced)
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/calculations/scenarios?sales_variation=${salesVariation}&cost_variation=${costVariation}`);
        setBackendScenarios(res.data);
      } catch (err) {
        console.error('Error refetching scenarios:', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [salesVariation, costVariation, projectId, loading]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/report/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_ejecutivo_${currentProject?.name || 'proyecto'}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      toast.success('Reporte PDF descargado');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Error al generar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  const downloadCompletePDF = async () => {
    setDownloadingComplete(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/report/pdf/complete`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_completo_${currentProject?.name || 'proyecto'}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      toast.success('Reporte completo descargado');
    } catch (err) {
      console.error('Error downloading complete PDF:', err);
      toast.error('Error al generar el PDF completo');
    } finally {
      setDownloadingComplete(false);
    }
  };

  const currency = currentProject?.specifications?.currency || 'MXN';
  const wacc = baseValuation?.wacc || 0.12;
  const costOfEquity = currentProject?.financing?.cost_of_equity || wacc;
  const baseNPV = baseValuation?.npv || 0;
  const baseIRR = baseValuation?.project_irr || baseValuation?.irr || 0;
  const baseEquityIRR = baseValuation?.equity_irr || 0;

  // Calculate scenarios based on user-defined variations
  const scenarios = useMemo(() => {
    if (!baseValuation) return {};
    
    // Simplified scenario calculation (in real app, this would call backend)
    const calculateScenario = (salesMult, costMult) => {
      const npvChange = baseNPV * (1 + (salesMult - 1) * 2 - (costMult - 1) * 1.5);
      const irrChange = baseIRR * (1 + (salesMult - 1) * 0.5 - (costMult - 1) * 0.3);
      const equityIrrChange = baseEquityIRR * (1 + (salesMult - 1) * 0.6 - (costMult - 1) * 0.35);
      
      const criteria = {
        tirProyecto: irrChange > wacc,
        tirAccionista: equityIrrChange > costOfEquity,
        flujoPositivo: npvChange > -baseNPV * 0.5,
        vanPositivo: npvChange > 0
      };
      
      const passed = Object.values(criteria).filter(Boolean).length;
      
      return {
        npv: npvChange,
        irr: irrChange,
        equityIrr: equityIrrChange,
        criteria,
        passed,
        total: 4,
        score: (passed / 4) * 100,
        decision: passed === 4 ? 'ACEPTADO' : 'NO ACEPTADO'
      };
    };
    
    return {
      optimistic: calculateScenario(1 + salesVariation/100, 1 - costVariation/100),
      base: calculateScenario(1, 1),
      pessimistic: calculateScenario(1 - salesVariation/100, 1 + costVariation/100)
    };
  }, [baseValuation, baseNPV, baseIRR, baseEquityIRR, salesVariation, costVariation, wacc, costOfEquity]);

  // Generate sensitivity matrix
  const sensitivityMatrix = useMemo(() => {
    if (!baseValuation) return [];
    
    const steps = [-2, -1, 0, 1, 2];
    const salesSteps = steps.map(s => s * (salesVariation / 2));
    const costSteps = steps.map(s => s * (costVariation / 2));
    
    return costSteps.map((costDelta, rowIdx) => {
      const row = { costChange: costDelta };
      salesSteps.forEach((salesDelta, colIdx) => {
        const salesMult = 1 + salesDelta / 100;
        const costMult = 1 + costDelta / 100;
        
        const npvChange = baseNPV * (1 + (salesMult - 1) * 2 - (costMult - 1) * 1.5);
        const irrChange = baseIRR * (1 + (salesMult - 1) * 0.5 - (costMult - 1) * 0.3);
        const equityIrrChange = baseEquityIRR * (1 + (salesMult - 1) * 0.6 - (costMult - 1) * 0.35);
        
        const criteria = {
          tirProyecto: { passed: irrChange > wacc, value: irrChange, benchmark: wacc },
          tirAccionista: { passed: equityIrrChange > costOfEquity, value: equityIrrChange, benchmark: costOfEquity },
          flujoPositivo: { passed: npvChange > -baseNPV * 0.5, value: npvChange > -baseNPV * 0.5 },
          vanPositivo: { passed: npvChange > 0, value: npvChange }
        };
        
        const passed = Object.values(criteria).filter(c => c.passed).length;
        
        row[`sales_${colIdx}`] = {
          salesChange: salesDelta,
          costChange: costDelta,
          npv: npvChange,
          irr: irrChange,
          equityIrr: equityIrrChange,
          criteria,
          passed,
          total: 4,
          score: (passed / 4) * 100,
          isBase: salesDelta === 0 && costDelta === 0
        };
      });
      return row;
    });
  }, [baseValuation, baseNPV, baseIRR, baseEquityIRR, salesVariation, costVariation, wacc, costOfEquity]);

  // Breakeven comparison data
  const breakevenComparison = useMemo(() => {
    if (!breakeven || !baseValuation) return [];
    
    const projected = breakeven.projected_annual_units || 0;
    const beUnits = breakeven.breakeven_units || 0;
    const projectedRevenue = breakeven.projected_revenue || baseNPV * 0.3;
    const beRevenue = breakeven.breakeven_revenue || 0;
    
    return [
      {
        metric: 'Unidades Anuales',
        base: projected,
        equilibrio: beUnits,
        diferencia: projected - beUnits,
        porcentaje: beUnits > 0 ? ((projected - beUnits) / beUnits * 100) : 0
      },
      {
        metric: 'Ingresos',
        base: projectedRevenue,
        equilibrio: beRevenue,
        diferencia: projectedRevenue - beRevenue,
        porcentaje: beRevenue > 0 ? ((projectedRevenue - beRevenue) / beRevenue * 100) : 0
      }
    ];
  }, [breakeven, baseValuation, baseNPV]);

  // Average monthly net income per scenario (using average operating months across all years)
  const avgOpMonths = useMemo(() => {
    const om = currentProject?.operating_months || [];
    if (om.length === 0) return 12;
    return om.reduce((s, v) => s + (v || 12), 0) / om.length;
  }, [currentProject]);

  const monthlyNetIncome = useMemo(() => {
    if (!incomeYears || incomeYears.length === 0) return { base: 0, optimistic: 0, pessimistic: 0 };
    const avgAnnual = incomeYears.reduce((sum, y) => sum + (y.net_income || 0), 0) / incomeYears.length;
    const salesMult = salesVariation / 100;
    const costMult = costVariation / 100;
    return {
      base: avgAnnual / avgOpMonths,
      optimistic: (avgAnnual * (1 + salesMult * 1.5 - costMult * 0.8)) / avgOpMonths,
      pessimistic: (avgAnnual * (1 - salesMult * 1.5 + costMult * 0.8)) / avgOpMonths,
    };
  }, [incomeYears, salesVariation, costVariation, avgOpMonths]);

  // Breakeven divisor based on view, using operating months
  const year1OpMonths = breakeven?.year1_operating_months || (currentProject?.operating_months?.[0]) || 12;
  const beDaysPerMonth = breakeven?.days_per_month || ((currentProject?.specifications?.operates_seven_days !== false ? 365 : 312) / 12);
  const beDivisor = breakevenView === 'mensual' ? year1OpMonths : breakevenView === 'diario' ? (beDaysPerMonth * year1OpMonths) : 1;
  const beLabel = breakevenView === 'mensual' ? 'Mensual' : breakevenView === 'diario' ? 'Diario' : 'Anual';
  const beUnitLabel = breakevenView === 'mensual' ? 'unidades/mes' : breakevenView === 'diario' ? 'unidades/día' : 'unidades/año';

  const handleCellClick = (cellData) => {
    setSelectedCell(cellData);
    setShowDetailPopup(true);
  };

  const goToPrev = () => navigate(`/app/project/${projectId}/valuation`);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Calculando escenarios...</div>
      </div>
    );
  }

  const salesStepLabels = [-salesVariation, -salesVariation/2, 0, salesVariation/2, salesVariation];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scenarios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-400 rounded text-xs font-medium">ANÁLISIS AVANZADO</span>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">Escenarios y Sensibilidad</h1>
          <p className="text-muted-foreground mt-1">
            Análisis de riesgo y viabilidad bajo diferentes condiciones
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={downloadPDF} 
            disabled={downloading}
            variant="outline"
            className="gap-2"
            data-testid="download-pdf-btn"
          >
            <FileText className="w-4 h-4" />
            {downloading ? 'Generando...' : 'Resumen Ejecutivo'}
          </Button>
          <Button 
            onClick={downloadCompletePDF} 
            disabled={downloadingComplete}
            className="gap-2 bg-gradient-to-r from-primary to-cyan-600"
            data-testid="download-complete-pdf-btn"
          >
            <Download className="w-4 h-4" />
            {downloadingComplete ? 'Generando...' : 'Reporte Completo'}
          </Button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="flex gap-3 justify-end -mt-2">
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => exportScenariosToExcel(
            scenarios, 
            breakeven, 
            currentProject?.name || 'Proyecto', 
            currency,
            backendScenarios
          )}
          data-testid="export-scenarios-excel-btn"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Excel
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50 p-1">
          <TabsTrigger value="scenarios" className="data-[state=active]:bg-violet-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            ESCENARIOS
          </TabsTrigger>
          <TabsTrigger value="sensitivity" className="data-[state=active]:bg-cyan-600">
            <Target className="w-4 h-4 mr-2" />
            SENSIBILIDAD
          </TabsTrigger>
          <TabsTrigger value="breakeven" className="data-[state=active]:bg-emerald-600">
            <TrendingUp className="w-4 h-4 mr-2" />
            PUNTO DE EQUILIBRIO
          </TabsTrigger>
        </TabsList>

        {/* ==================== ESCENARIOS TAB ==================== */}
        <TabsContent value="scenarios" className="mt-6 space-y-6">
          {/* Configuration Card */}
          <Card className="border-violet-500/30 bg-gradient-to-br from-violet-950/30 to-background">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-violet-400" />
                Configuración de Rangos
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Define el porcentaje de variación para los escenarios optimista y pesimista
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Variación de Ventas</Label>
                    <span className="text-xl font-bold text-violet-400">±{salesVariation}%</span>
                  </div>
                  <Slider
                    value={[salesVariation]}
                    onValueChange={(v) => setSalesVariation(v[0])}
                    min={5}
                    max={50}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Pesimista: -{salesVariation}%</span>
                    <span>Optimista: +{salesVariation}%</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Variación de Costos</Label>
                    <span className="text-xl font-bold text-orange-400">±{costVariation}%</span>
                  </div>
                  <Slider
                    value={[costVariation]}
                    onValueChange={(v) => setCostVariation(v[0])}
                    min={5}
                    max={30}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Optimista: -{costVariation}%</span>
                    <span>Pesimista: +{costVariation}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scenarios Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Optimistic */}
            <Card className="border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-background overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl" />
              <CardHeader className="pb-2 relative">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-emerald-400">Optimista</CardTitle>
                    <p className="text-xs text-muted-foreground">+{salesVariation}% ventas, -{costVariation}% costos</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 relative">
                <div>
                  <p className="text-xs text-muted-foreground">VAN (NPV)</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatCurrency(scenarios.optimistic?.npv || 0, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">TIR Proyecto</p>
                  <p className="text-lg font-bold">{formatCappedPercent(scenarios.optimistic?.irr || 0)}</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg">
                  <p className="text-xs text-emerald-400/80">Utilidad Neta Mensual Promedio</p>
                  <p className="text-lg font-bold text-emerald-400" data-testid="optimistic-monthly-income">
                    {formatCurrency(monthlyNetIncome.optimistic, currency)}
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-1 pt-2 border-t border-emerald-500/20">
                  {scenarios.optimistic?.criteria && Object.entries(scenarios.optimistic.criteria).map(([key, passed], idx) => (
                    <div key={idx} className={cn(
                      "h-2 rounded-full",
                      passed ? "bg-emerald-500" : "bg-red-500/50"
                    )} title={key} />
                  ))}
                </div>
                
                <div className={cn(
                  "flex items-center gap-2 p-3 rounded-lg",
                  scenarios.optimistic?.decision === 'ACEPTADO' ? "bg-emerald-500/20" : "bg-red-500/20"
                )}>
                  {scenarios.optimistic?.decision === 'ACEPTADO' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <div>
                    <span className={cn(
                      "font-bold",
                      scenarios.optimistic?.decision === 'ACEPTADO' ? 'text-emerald-400' : 'text-red-400'
                    )}>
                      {scenarios.optimistic?.decision}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({scenarios.optimistic?.passed}/{scenarios.optimistic?.total} criterios)
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Base */}
            <Card className="border-blue-500/40 bg-gradient-to-br from-blue-950/40 to-background overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
              <CardHeader className="pb-2 relative">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-blue-400">Base</CardTitle>
                    <p className="text-xs text-muted-foreground">Sin cambios</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 relative">
                <div>
                  <p className="text-xs text-muted-foreground">VAN (NPV)</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {formatCurrency(scenarios.base?.npv || 0, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">TIR Proyecto</p>
                  <p className="text-lg font-bold">{formatCappedPercent(scenarios.base?.irr || 0)}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <p className="text-xs text-blue-400/80">Utilidad Neta Mensual Promedio</p>
                  <p className="text-lg font-bold text-blue-400" data-testid="base-monthly-income">
                    {formatCurrency(monthlyNetIncome.base, currency)}
                  </p>
                </div>
                
                {/* Criteria Mini-indicators */}
                <div className="grid grid-cols-4 gap-1 pt-2 border-t border-blue-500/20">
                  {scenarios.base?.criteria && Object.entries(scenarios.base.criteria).map(([key, passed], idx) => (
                    <div key={idx} className={cn(
                      "h-2 rounded-full",
                      passed ? "bg-blue-500" : "bg-red-500/50"
                    )} title={key} />
                  ))}
                </div>
                
                <div className={cn(
                  "flex items-center gap-2 p-3 rounded-lg",
                  scenarios.base?.decision === 'ACEPTADO' ? "bg-blue-500/20" : "bg-red-500/20"
                )}>
                  {scenarios.base?.decision === 'ACEPTADO' ? (
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <div>
                    <span className={cn(
                      "font-bold",
                      scenarios.base?.decision === 'ACEPTADO' ? 'text-blue-400' : 'text-red-400'
                    )}>
                      {scenarios.base?.decision}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({scenarios.base?.passed}/{scenarios.base?.total} criterios)
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pessimistic */}
            <Card className="border-red-500/40 bg-gradient-to-br from-red-950/40 to-background overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-2xl" />
              <CardHeader className="pb-2 relative">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <CardTitle className="text-red-400">Pesimista</CardTitle>
                    <p className="text-xs text-muted-foreground">-{salesVariation}% ventas, +{costVariation}% costos</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 relative">
                <div>
                  <p className="text-xs text-muted-foreground">VAN (NPV)</p>
                  <p className={cn(
                    "text-2xl font-bold",
                    (scenarios.pessimistic?.npv || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                  )}>
                    {formatCurrency(scenarios.pessimistic?.npv || 0, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">TIR Proyecto</p>
                  <p className="text-lg font-bold">{formatCappedPercent(scenarios.pessimistic?.irr || 0)}</p>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <p className="text-xs text-red-400/80">Utilidad Neta Mensual Promedio</p>
                  <p className={cn("text-lg font-bold", monthlyNetIncome.pessimistic >= 0 ? "text-red-400" : "text-red-500")} data-testid="pessimistic-monthly-income">
                    {formatCurrency(monthlyNetIncome.pessimistic, currency)}
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-1 pt-2 border-t border-red-500/20">
                  {scenarios.pessimistic?.criteria && Object.entries(scenarios.pessimistic.criteria).map(([key, passed], idx) => (
                    <div key={idx} className={cn(
                      "h-2 rounded-full",
                      passed ? "bg-emerald-500" : "bg-red-500/50"
                    )} title={key} />
                  ))}
                </div>
                
                <div className={cn(
                  "flex items-center gap-2 p-3 rounded-lg",
                  scenarios.pessimistic?.decision === 'ACEPTADO' ? "bg-emerald-500/20" : "bg-red-500/20"
                )}>
                  {scenarios.pessimistic?.decision === 'ACEPTADO' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <div>
                    <span className={cn(
                      "font-bold",
                      scenarios.pessimistic?.decision === 'ACEPTADO' ? 'text-emerald-400' : 'text-red-400'
                    )}>
                      {scenarios.pessimistic?.decision}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({scenarios.pessimistic?.passed}/{scenarios.pessimistic?.total} criterios)
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Risk Assessment */}
          <Card className={cn(
            "border-2",
            scenarios.pessimistic?.decision === 'ACEPTADO' 
              ? "border-emerald-500/50 bg-gradient-to-r from-emerald-950/30 to-background" 
              : "border-amber-500/50 bg-gradient-to-r from-amber-950/30 to-background"
          )}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {scenarios.pessimistic?.decision === 'ACEPTADO' ? (
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-7 h-7 text-emerald-400" />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-7 h-7 text-amber-400" />
                  </div>
                )}
                <div>
                  <h3 className={cn(
                    "text-xl font-bold mb-2",
                    scenarios.pessimistic?.decision === 'ACEPTADO' ? "text-emerald-400" : "text-amber-400"
                  )}>
                    {scenarios.pessimistic?.decision === 'ACEPTADO' 
                      ? '✓ Proyecto Robusto ante Adversidad'
                      : '⚠ Sensible a Condiciones Adversas'}
                  </h3>
                  <p className="text-muted-foreground">
                    {scenarios.pessimistic?.decision === 'ACEPTADO' 
                      ? `Incluso con ${salesVariation}% menos ventas y ${costVariation}% más costos, el proyecto sigue siendo viable. Esto indica baja sensibilidad al riesgo de mercado.`
                      : `En condiciones adversas (-${salesVariation}% ventas, +${costVariation}% costos), el proyecto podría no ser viable. Se recomienda establecer reservas de contingencia.`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Perpetuity Comparison */}
          {backendScenarios?.perpetuity_active && (
            <Card className="border-violet-500/30" data-testid="perpetuity-comparison">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Infinity className="w-5 h-5 text-violet-400" />
                  Impacto de la Perpetuidad en Escenarios
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Comparacion de cada escenario con y sin el valor terminal (perpetuidad).
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="perpetuity-comparison-table">
                    <thead>
                      <tr className="border-b border-border/30">
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Escenario</th>
                        <th className="text-center py-2 px-3 font-medium text-violet-400" colSpan="2">Con Perpetuidad</th>
                        <th className="text-center py-2 px-3 font-medium text-muted-foreground" colSpan="2">Sin Perpetuidad</th>
                        <th className="text-center py-2 px-3 font-medium text-muted-foreground">Valor Terminal</th>
                      </tr>
                      <tr className="border-b border-border/20 text-xs text-muted-foreground">
                        <th className="text-left py-1 px-3"></th>
                        <th className="text-center py-1 px-3">VAN</th>
                        <th className="text-center py-1 px-3">TIR</th>
                        <th className="text-center py-1 px-3">VAN</th>
                        <th className="text-center py-1 px-3">TIR</th>
                        <th className="text-center py-1 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {['optimistic', 'base', 'pessimistic'].map(key => {
                        const s = backendScenarios?.scenarios?.[key];
                        if (!s) return null;
                        const labels = { optimistic: 'Optimista', base: 'Base', pessimistic: 'Pesimista' };
                        const colors = { optimistic: 'text-emerald-400', base: 'text-primary', pessimistic: 'text-amber-400' };
                        return (
                          <tr key={key} className="border-b border-border/10 hover:bg-secondary/20">
                            <td className={`py-2.5 px-3 font-medium ${colors[key]}`}>{labels[key]}</td>
                            <td className="text-center py-2.5 px-3 tabular-nums font-semibold text-violet-400">
                              {formatCurrency(s.npv, currency)}
                            </td>
                            <td className="text-center py-2.5 px-3 tabular-nums font-semibold text-violet-400">
                              {s.irr?.toFixed(1)}%
                            </td>
                            <td className="text-center py-2.5 px-3 tabular-nums">
                              {formatCurrency(s.npv_without_perpetuity, currency)}
                            </td>
                            <td className="text-center py-2.5 px-3 tabular-nums">
                              {s.irr_without_perpetuity?.toFixed(1)}%
                            </td>
                            <td className="text-center py-2.5 px-3 tabular-nums text-xs text-violet-300">
                              {formatCurrency(s.terminal_value || 0, currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  La perpetuidad asume que el negocio opera indefinidamente. Los valores con perpetuidad reflejan el valor total del negocio como empresa en marcha.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== SENSIBILIDAD TAB ==================== */}
        <TabsContent value="sensitivity" className="mt-6 space-y-6">
          <Card className="border-cyan-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" />
                Matriz de Sensibilidad
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Haz clic en cualquier celda para ver el desglose completo de métricas
              </p>
            </CardHeader>
            <CardContent>
              {/* Matrix Header */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-3 text-left bg-secondary/30 rounded-tl-lg">
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">Costos ↓ / Ventas →</span>
                        </div>
                      </th>
                      {salesStepLabels.map((label, idx) => (
                        <th key={idx} className={cn(
                          "p-3 text-center bg-secondary/30 text-sm font-medium",
                          label === 0 ? "text-blue-400" : label > 0 ? "text-emerald-400" : "text-red-400",
                          idx === salesStepLabels.length - 1 && "rounded-tr-lg"
                        )}>
                          {label > 0 ? '+' : ''}{label}%
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sensitivityMatrix.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className={cn(
                          "p-3 bg-secondary/20 text-sm font-medium",
                          row.costChange === 0 ? "text-blue-400" : row.costChange < 0 ? "text-emerald-400" : "text-red-400",
                          rowIdx === sensitivityMatrix.length - 1 && "rounded-bl-lg"
                        )}>
                          {row.costChange > 0 ? '+' : ''}{row.costChange}%
                        </td>
                        {[0, 1, 2, 3, 4].map((colIdx) => {
                          const cell = row[`sales_${colIdx}`];
                          if (!cell) return <td key={colIdx} className="p-2" />;
                          
                          const isAccepted = cell.passed === 4;
                          const score = cell.score;
                          
                          return (
                            <td 
                              key={colIdx}
                              className={cn(
                                "p-2 text-center cursor-pointer transition-all hover:scale-105",
                                cell.isBase && "ring-2 ring-blue-500/50",
                                rowIdx === sensitivityMatrix.length - 1 && colIdx === 4 && "rounded-br-lg"
                              )}
                              onClick={() => handleCellClick(cell)}
                            >
                              <div className={cn(
                                "p-3 rounded-lg transition-all",
                                isAccepted 
                                  ? "bg-emerald-500/20 hover:bg-emerald-500/30" 
                                  : score >= 50 
                                    ? "bg-amber-500/20 hover:bg-amber-500/30"
                                    : "bg-red-500/20 hover:bg-red-500/30"
                              )}>
                                <div className={cn(
                                  "text-xs font-bold mb-1",
                                  isAccepted ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400"
                                )}>
                                  {isAccepted ? '✓ ACEPTADO' : '✗ NO ACEPTADO'}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  {cell.passed}/4 ({score.toFixed(0)}%)
                                </div>
                                {cell.isBase && (
                                  <div className="text-[9px] text-blue-400 font-medium mt-1">BASE</div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-secondary">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-500/30" />
                  <span className="text-xs text-muted-foreground">4/4 Criterios (Aceptado)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-500/30" />
                  <span className="text-xs text-muted-foreground">2-3/4 Criterios</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500/30" />
                  <span className="text-xs text-muted-foreground">0-1/4 Criterios</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Explanation Card */}
          <Card className="bg-secondary/20">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                ¿Cómo interpretar la matriz?
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <p className="mb-2">
                    <span className="text-cyan-400 font-medium">Filas (Costos):</span> Variación porcentual de costos. 
                    Valores negativos = reducción de costos (favorable).
                  </p>
                  <p>
                    <span className="text-cyan-400 font-medium">Columnas (Ventas):</span> Variación porcentual de ventas. 
                    Valores positivos = aumento de ventas (favorable).
                  </p>
                </div>
                <div>
                  <p className="mb-2">
                    <span className="text-emerald-400 font-medium">Celda verde:</span> El proyecto cumple todos los criterios y es viable.
                  </p>
                  <p>
                    <span className="text-amber-400 font-medium">Celda amarilla/roja:</span> No cumple todos los criterios. 
                    Haz clic para ver qué métricas fallan.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== PUNTO DE EQUILIBRIO TAB ==================== */}
        <TabsContent value="breakeven" className="mt-6 space-y-6">
          {/* View Filter */}
          <div className="flex items-center gap-2" data-testid="breakeven-view-filter">
            <span className="text-sm text-muted-foreground mr-2">Vista:</span>
            {['anual', 'mensual', 'diario'].map((v) => (
              <Button key={v} size="sm" variant={breakevenView === v ? 'default' : 'outline'}
                onClick={() => setBreakevenView(v)} className="capitalize">
                {v === 'anual' ? 'Anual' : v === 'mensual' ? 'Mensual' : 'Diario'}
              </Button>
            ))}
          </div>

          {/* Perpetuity Disclaimer */}
          {backendScenarios?.perpetuity_active && (
            <Card className="border-amber-500/30 bg-amber-500/5" data-testid="breakeven-perpetuity-disclaimer">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-400">Este analisis NO incluye la perpetuidad</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    El punto de equilibrio se calcula unicamente sobre la vida del proyecto ({currentProject?.specifications?.project_life_years || 5} años). 
                    El valor terminal (perpetuidad) no afecta este calculo ya que mide el volumen minimo de ventas para cubrir costos operativos, no la valuacion del negocio.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-950/40 to-background border-emerald-500/30">
              <CardContent className="p-5">
                <Target className="w-8 h-8 text-emerald-400 mb-3" />
                <p className="text-xs text-emerald-400/80 mb-1">Punto de Equilibrio</p>
                <p className="text-2xl font-bold">{formatNumber(Math.round((breakeven?.breakeven_units || 0) / beDivisor))}</p>
                <p className="text-xs text-muted-foreground">{beUnitLabel}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-950/40 to-background border-blue-500/30">
              <CardContent className="p-5">
                <DollarSign className="w-8 h-8 text-blue-400 mb-3" />
                <p className="text-xs text-blue-400/80 mb-1">Ingresos Mínimos</p>
                <p className="text-2xl font-bold">{formatCurrency((breakeven?.breakeven_revenue || 0) / beDivisor, currency)}</p>
                <p className="text-xs text-muted-foreground">para cubrir costos ({beLabel.toLowerCase()})</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-violet-950/40 to-background border-violet-500/30">
              <CardContent className="p-5">
                <Percent className="w-8 h-8 text-violet-400 mb-3" />
                <p className="text-xs text-violet-400/80 mb-1">Margen de Seguridad</p>
                <p className={cn(
                  "text-2xl font-bold",
                  (breakeven?.margin_of_safety || 0) > 0.2 ? "text-emerald-400" : "text-amber-400"
                )}>
                  {formatPercent(breakeven?.margin_of_safety || 0)}
                </p>
                <p className="text-xs text-muted-foreground">sobre equilibrio</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-950/40 to-background border-amber-500/30">
              <CardContent className="p-5">
                <TrendingDown className="w-8 h-8 text-amber-400 mb-3" />
                <p className="text-xs text-amber-400/80 mb-1">Costos Fijos</p>
                <p className="text-2xl font-bold">{formatCurrency((breakeven?.total_fixed_costs || 0) / beDivisor, currency)}</p>
                <p className="text-xs text-muted-foreground">{beLabel.toLowerCase()}es</p>
              </CardContent>
            </Card>
          </div>

          {/* Comparative Charts - 3 Bars */}
          <div className="grid grid-cols-2 gap-6">
            {/* Chart 1: Units */}
            <Card className="border-blue-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  Unidades ({beLabel})
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  De menor a mayor: Equilibrio Operativo → TIR=WACC → Real
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={[
                    {
                      name: 'Equilibrio Op.',
                      valor: Math.round((breakeven?.breakeven_units || 0) / beDivisor),
                      fill: '#10B981'
                    },
                    {
                      name: 'TIR=WACC',
                      valor: Math.round((breakeven?.breakeven_wacc_units || 0) / beDivisor),
                      fill: '#F59E0B'
                    },
                    {
                      name: 'Real',
                      valor: Math.round((breakeven?.projected_annual_units || 0) / beDivisor),
                      fill: '#3B82F6'
                    }
                  ]} margin={{ top: 25, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} />
                    <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatNumber(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                      formatter={(value) => [formatNumber(value), 'Unidades']}
                    />
                    <Bar dataKey="valor" radius={[8, 8, 0, 0]} barSize={60}>
                      <Cell fill="#10B981" />
                      <Cell fill="#F59E0B" />
                      <Cell fill="#3B82F6" />
                      <LabelList dataKey="valor" position="top" formatter={(v) => formatNumber(v)} fill="#E2E8F0" fontSize={11} fontWeight={600} />
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-2 bg-emerald-500/10 rounded">
                    <span className="text-emerald-400">Equilibrio Op.</span>
                    <p className="font-bold mt-1">{formatNumber(Math.round((breakeven?.breakeven_units || 0) / beDivisor))}</p>
                    <p className="text-[10px] text-muted-foreground">Sin pérdida ni ganancia</p>
                  </div>
                  <div className="p-2 bg-amber-500/10 rounded">
                    <span className="text-amber-400">TIR=WACC</span>
                    <p className="font-bold mt-1">{formatNumber(Math.round((breakeven?.breakeven_wacc_units || 0) / beDivisor))}</p>
                    <p className="text-[10px] text-muted-foreground">Retorno = Costo Capital</p>
                  </div>
                  <div className="p-2 bg-blue-500/10 rounded">
                    <span className="text-blue-400">Real</span>
                    <p className="font-bold mt-1">{formatNumber(Math.round((breakeven?.projected_annual_units || 0) / beDivisor))}</p>
                    <p className="text-[10px] text-muted-foreground">Proyección actual</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chart 2: Revenue */}
            <Card className="border-emerald-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  Ingresos ({beLabel})
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  De menor a mayor: Equilibrio Operativo → TIR=WACC → Real
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={[
                    {
                      name: 'Equilibrio Op.',
                      valor: (breakeven?.breakeven_revenue || 0) / beDivisor,
                      fill: '#10B981'
                    },
                    {
                      name: 'TIR=WACC',
                      valor: (breakeven?.breakeven_wacc_revenue || 0) / beDivisor,
                      fill: '#F59E0B'
                    },
                    {
                      name: 'Real',
                      valor: (breakeven?.projected_annual_revenue || 0) / beDivisor,
                      fill: '#3B82F6'
                    }
                  ]} margin={{ top: 25, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} />
                    <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                      formatter={(value) => [formatCurrency(value, currency), 'Ingresos']}
                    />
                    <Bar dataKey="valor" radius={[8, 8, 0, 0]} barSize={60}>
                      <Cell fill="#10B981" />
                      <Cell fill="#F59E0B" />
                      <Cell fill="#3B82F6" />
                      <LabelList dataKey="valor" position="top" formatter={(v) => formatChartValue(v)} fill="#E2E8F0" fontSize={11} fontWeight={600} />
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-2 bg-emerald-500/10 rounded">
                    <span className="text-emerald-400">Equilibrio Op.</span>
                    <p className="font-bold mt-1">{formatCurrency((breakeven?.breakeven_revenue || 0) / beDivisor, currency)}</p>
                    <p className="text-[10px] text-muted-foreground">Ingresos mínimos</p>
                  </div>
                  <div className="p-2 bg-amber-500/10 rounded">
                    <span className="text-amber-400">TIR=WACC</span>
                    <p className="font-bold mt-1">{formatCurrency((breakeven?.breakeven_wacc_revenue || 0) / beDivisor, currency)}</p>
                    <p className="text-[10px] text-muted-foreground">Para cubrir costo capital</p>
                  </div>
                  <div className="p-2 bg-blue-500/10 rounded">
                    <span className="text-blue-400">Real</span>
                    <p className="font-bold mt-1">{formatCurrency((breakeven?.projected_annual_revenue || 0) / beDivisor, currency)}</p>
                    <p className="text-[10px] text-muted-foreground">Proyección actual</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress indicators */}
          <Card className="border-violet-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="w-4 h-4 text-violet-400" />
                Visualización de Margen ({beLabel})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Unidades</span>
                  <span className="font-medium">
                    <span className="text-blue-400">{formatNumber(Math.round((breakeven?.projected_annual_units || 0) / beDivisor))}</span>
                    {' vs '}
                    <span className="text-emerald-400">{formatNumber(Math.round((breakeven?.breakeven_units || 0) / beDivisor))}</span>
                    {' (equilibrio)'}
                  </span>
                </div>
                <div className="relative h-6 bg-secondary/50 rounded-full overflow-hidden">
                  <div 
                    className="absolute h-full bg-emerald-500/50 rounded-full"
                    style={{ 
                      width: `${Math.min(100, ((breakeven?.breakeven_units || 0) / (breakeven?.projected_annual_units || 1)) * 100)}%` 
                    }}
                  />
                  <div 
                    className="absolute h-full bg-blue-500 rounded-full transition-all"
                    style={{ 
                      width: `100%`,
                      opacity: 0.8
                    }}
                  />
                  {/* Equilibrium marker */}
                  <div 
                    className="absolute top-0 h-full w-1 bg-emerald-400"
                    style={{ 
                      left: `${Math.min(95, ((breakeven?.breakeven_units || 0) / (breakeven?.projected_annual_units || 1)) * 100)}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0</span>
                  <span className="text-emerald-400">← Punto de Equilibrio</span>
                  <span>Proyectado →</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Comparison Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalle de Comparación ({beLabel})</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary">
                    <th className="text-left py-3 text-sm text-muted-foreground">Métrica</th>
                    <th className="text-right py-3 text-sm text-blue-400">Proyectado</th>
                    <th className="text-right py-3 text-sm text-emerald-400">Equilibrio</th>
                    <th className="text-right py-3 text-sm text-muted-foreground">Diferencia</th>
                    <th className="text-right py-3 text-sm text-muted-foreground">Margen %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-secondary/50">
                    <td className="py-4 font-medium">Unidades ({beLabel})</td>
                    <td className="py-4 text-right font-mono text-blue-400">
                      {formatNumber(Math.round((breakeven?.projected_annual_units || 0) / beDivisor))}
                    </td>
                    <td className="py-4 text-right font-mono text-emerald-400">
                      {formatNumber(Math.round((breakeven?.breakeven_units || 0) / beDivisor))}
                    </td>
                    <td className="py-4 text-right font-mono text-muted-foreground">
                      +{formatNumber(Math.round(((breakeven?.projected_annual_units || 0) - (breakeven?.breakeven_units || 0)) / beDivisor))}
                    </td>
                    <td className="py-4 text-right">
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-bold",
                        (breakeven?.margin_of_safety || 0) > 0.2 ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
                      )}>
                        +{((breakeven?.margin_of_safety || 0) * 100).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-secondary/50">
                    <td className="py-4 font-medium">Ingresos ({beLabel})</td>
                    <td className="py-4 text-right font-mono text-blue-400">
                      {formatCurrency((breakeven?.projected_revenue || baseNPV * 0.3) / beDivisor, currency)}
                    </td>
                    <td className="py-4 text-right font-mono text-emerald-400">
                      {formatCurrency((breakeven?.breakeven_revenue || 0) / beDivisor, currency)}
                    </td>
                    <td className="py-4 text-right font-mono text-muted-foreground">
                      +{formatCurrency(((breakeven?.projected_revenue || baseNPV * 0.3) - (breakeven?.breakeven_revenue || 0)) / beDivisor, currency)}
                    </td>
                    <td className="py-4 text-right">
                      <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs font-bold">
                        +{(breakeven?.breakeven_revenue > 0 
                          ? (((breakeven?.projected_revenue || baseNPV * 0.3) - breakeven.breakeven_revenue) / breakeven.breakeven_revenue * 100) 
                          : 0).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium">Margen Contribución</td>
                    <td className="py-4 text-right font-mono text-blue-400" colSpan={2}>
                      {formatCurrency(breakeven?.avg_contribution_margin || 0, currency)} por unidad
                    </td>
                    <td className="py-4 text-right font-mono text-muted-foreground" colSpan={2}>
                      Ratio: {formatPercent(breakeven?.contribution_margin_ratio || 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Popup */}
      <Dialog open={showDetailPopup} onOpenChange={setShowDetailPopup}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-400" />
              Detalle del Escenario
            </DialogTitle>
          </DialogHeader>
          
          {selectedCell && (
            <div className="space-y-4">
              {/* Scenario Info */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Variación Ventas:</span>
                    <span className={cn(
                      "ml-2 font-bold",
                      selectedCell.salesChange > 0 ? "text-emerald-400" : selectedCell.salesChange < 0 ? "text-red-400" : "text-blue-400"
                    )}>
                      {selectedCell.salesChange > 0 ? '+' : ''}{selectedCell.salesChange}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Variación Costos:</span>
                    <span className={cn(
                      "ml-2 font-bold",
                      selectedCell.costChange < 0 ? "text-emerald-400" : selectedCell.costChange > 0 ? "text-red-400" : "text-blue-400"
                    )}>
                      {selectedCell.costChange > 0 ? '+' : ''}{selectedCell.costChange}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Métricas Financieras</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-secondary/20 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">VAN</p>
                    <p className={cn(
                      "text-lg font-bold",
                      selectedCell.npv > 0 ? "text-emerald-400" : "text-red-400"
                    )}>
                      {formatCurrency(selectedCell.npv, currency)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/20 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">TIR Proyecto</p>
                    <p className="text-lg font-bold">{formatCappedPercent(selectedCell.irr)}</p>
                  </div>
                  <div className="p-3 bg-secondary/20 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">TIR Accionista</p>
                    <p className="text-lg font-bold">{formatCappedPercent(selectedCell.equityIrr)}</p>
                  </div>
                </div>
                {/* Utilidad Neta Mensual Promedio */}
                {(() => {
                  const avgAnnual = incomeYears.length > 0 
                    ? incomeYears.reduce((s, y) => s + (y.net_income || 0), 0) / incomeYears.length 
                    : 0;
                  const salesMult = 1 + (selectedCell.salesChange || 0) / 100;
                  const costMult = 1 + (selectedCell.costChange || 0) / 100;
                  const adjusted = avgAnnual * (1 + (salesMult - 1) * 1.5 - (costMult - 1) * 0.8);
                  const monthly = adjusted / avgOpMonths;
                  return (
                    <div className="p-3 bg-cyan-500/10 rounded-lg text-center mt-3" data-testid="sensitivity-monthly-income">
                      <p className="text-xs text-cyan-400/80">Utilidad Neta Mensual Promedio</p>
                      <p className={cn("text-xl font-bold", monthly >= 0 ? "text-cyan-400" : "text-red-400")}>
                        {formatCurrency(monthly, currency)}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Criteria Detail */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Criterios de Decisión ({selectedCell.passed}/4)</h4>
                <div className="space-y-2">
                  {selectedCell.criteria && Object.entries(selectedCell.criteria).map(([key, data], idx) => {
                    const labels = {
                      tirProyecto: 'TIR Proyecto > WACC',
                      tirAccionista: 'TIR Accionista > Ke',
                      flujoPositivo: 'Flujo Operativo Positivo',
                      vanPositivo: 'VAN Positivo'
                    };
                    
                    return (
                      <div 
                        key={idx} 
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg",
                          data.passed ? "bg-emerald-500/10" : "bg-red-500/10"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {data.passed ? (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-sm">{labels[key]}</span>
                        </div>
                        <span className={cn(
                          "text-xs font-bold px-2 py-1 rounded",
                          data.passed ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                        )}>
                          {data.passed ? 'APROBADO' : 'REPROBADO'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Final Score */}
              <div className={cn(
                "p-4 rounded-lg text-center",
                selectedCell.passed === 4 ? "bg-emerald-500/20" : "bg-red-500/20"
              )}>
                <p className="text-sm text-muted-foreground mb-1">Calificación Final</p>
                <p className={cn(
                  "text-3xl font-bold",
                  selectedCell.passed === 4 ? "text-emerald-400" : "text-red-400"
                )}>
                  {selectedCell.score.toFixed(0)}%
                </p>
                <p className={cn(
                  "text-sm font-medium mt-1",
                  selectedCell.passed === 4 ? "text-emerald-400" : "text-red-400"
                )}>
                  {selectedCell.passed === 4 ? '✓ PROYECTO ACEPTADO' : '✗ PROYECTO NO ACEPTADO'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goToPrev} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Volver a Valuacion
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPDF} disabled={downloading} className="gap-2" data-testid="bottom-pdf-executive-btn">
            <FileText className="w-4 h-4" />
            {downloading ? 'Generando...' : 'Resumen Ejecutivo'}
          </Button>
          <Button onClick={downloadCompletePDF} disabled={downloadingComplete} className="gap-2 bg-gradient-to-r from-primary to-cyan-600" data-testid="bottom-pdf-complete-btn">
            <Download className="w-4 h-4" />
            {downloadingComplete ? 'Generando...' : 'Reporte Completo'}
          </Button>
        </div>
      </div>
    </div>
  );
};
